import { HashContainer, HashContainerIterator } from "./Base";
import checkObject from "../../utils/checkObject";
import $checkWithinAccessParams from "../../utils/checkParams.macro";
import { throwIteratorAccessError } from "../../utils/throwError";
class HashMapIterator extends HashContainerIterator {
    constructor(node, header, container, iteratorType) {
        super(node, header, iteratorType);
        this.container = container;
    }
    get pointer() {
        if (this._node === this._header) {
            throwIteratorAccessError();
        }
        const self = this;
        return new Proxy([], {
            get(_, props) {
                if (props === '0')
                    return self._node._key;
                else if (props === '1')
                    return self._node._value;
            },
            set(_, props, newValue) {
                if (props !== '1') {
                    throw new TypeError('props must be 1');
                }
                self._node._value = newValue;
                return true;
            }
        });
    }
    copy() {
        return new HashMapIterator(this._node, this._header, this.container, this.iteratorType);
    }
}
class HashMap extends HashContainer {
    constructor(container = []) {
        super();
        const self = this;
        container.forEach(function (el) {
            self.setElement(el[0], el[1]);
        });
    }
    begin() {
        return new HashMapIterator(this._head, this._header, this);
    }
    end() {
        return new HashMapIterator(this._header, this._header, this);
    }
    rBegin() {
        return new HashMapIterator(this._tail, this._header, this, 1 /* IteratorType.REVERSE */);
    }
    rEnd() {
        return new HashMapIterator(this._header, this._header, this, 1 /* IteratorType.REVERSE */);
    }
    front() {
        if (this._length === 0)
            return;
        return [this._head._key, this._head._value];
    }
    back() {
        if (this._length === 0)
            return;
        return [this._tail._key, this._tail._value];
    }
    /**
     * @description Insert a key-value pair or set value by the given key.
     * @param key - The key want to insert.
     * @param value - The value want to set.
     * @param isObject - Tell us if the type of inserted key is `object` to improve efficiency.<br/>
     *                   If a `undefined` value is passed in, the type will be automatically judged.
     * @returns The size of container after setting.
     */
    setElement(key, value, isObject) {
        return this._set(key, value, isObject);
    }
    /**
     * @description Get the value of the element of the specified key.
     * @param key - The key want to search.
     * @param isObject - Tell us if the type of inserted key is `object` to improve efficiency.<br/>
     *                   If a `undefined` value is passed in, the type will be automatically judged.
     * @example
     * const val = container.getElementByKey(1);
     */
    getElementByKey(key, isObject) {
        if (isObject === undefined)
            isObject = checkObject(key);
        if (isObject) {
            const index = key[this.HASH_TAG];
            return index !== undefined ? this._objMap[index]._value : undefined;
        }
        const node = this._originMap[key];
        return node ? node._value : undefined;
    }
    getElementByPos(pos) {
        if (pos < 0 || pos > this._length - 1) {
            throw new RangeError();
        }
        let node = this._head;
        while (pos--) {
            node = node._next;
        }
        return [node._key, node._value];
    }
    /**
     * @description Check key if exist in container.
     * @param key - The element you want to search.
     * @param isObject - Tell us if the type of inserted key is `object` to improve efficiency.<br/>
     *                   If a `undefined` value is passed in, the type will be automatically judged.
     * @returns An iterator pointing to the element if found, or super end if not found.
     */
    find(key, isObject) {
        const node = this._findElementNode(key, isObject);
        return new HashMapIterator(node, this._header, this);
    }
    forEach(callback) {
        let index = 0;
        let node = this._head;
        while (node !== this._header) {
            callback([node._key, node._value], index++, this);
            node = node._next;
        }
    }
    *[Symbol.iterator]() {
        let node = this._head;
        while (node !== this._header) {
            yield [node._key, node._value];
            node = node._next;
        }
    }
}
export default HashMap;
